/**
 * ENG-6157 — Composio auth_config ↔ MCP-server linkage assertion.
 *
 * The Test endpoint and the ENG-6139 connectivity probe both verify that a
 * managed integration's connected account is ACTIVE and bound to the agent's
 * runtime `user_id`. Neither, pre-ENG-6157, checked the **auth_config linkage**:
 * a connected account is created under some auth_config A, while the agent's
 * wired MCP server resolves tool calls through whatever auth_config(s) the
 * server is bound to (B). When A ≠ B the account reads perfectly healthy on an
 * account GET — right entity, ACTIVE — yet every live tool call fails with
 * `No connected account found …` because the server resolves under B and finds
 * nothing. That false green sent the sherlock diagnosis down the wrong path.
 *
 * This pure assessment is the deterministic gate shared by both callers: given
 * the account's auth_config id and the auth_config ids the agent's wired server
 * resolves with, decide whether they link.
 *
 * Tri-state on purpose:
 *   - `true`  — account auth_config is among the server's bound configs.
 *   - `false` — CONFIRMED mismatch (incl. a server with no auth config bound at
 *               all): tool calls cannot resolve the account. Fail VERIFIED.
 *   - `null`  — INDETERMINATE: we couldn't fetch the server's bindings, or the
 *               account API didn't return its auth_config. Never downgrade a
 *               passing account/entity verdict to a false fail on missing data —
 *               a transient fetch blip must not flip an integration to `error`
 *               (which de-provisions the agent's tools). The caller keeps its
 *               prior verdict and may note linkage as unverified.
 */

export interface AuthConfigLinkageInput {
  /** The connected account's auth_config id (Composio `auth_config_id`). */
  accountAuthConfigId?: string | null;
  /**
   * The auth_config ids the agent's wired MCP server resolves with.
   *   - `null`/`undefined` → couldn't determine (fetch failed / unknown) → indeterminate.
   *   - `[]` → fetched successfully but the server has NO auth config bound →
   *            a real, confirmed broken linkage (it can resolve nothing).
   */
  serverAuthConfigIds?: string[] | null;
  /** The wired server id, for naming both sides in the verdict message. */
  serverId?: string | null;
}

export interface AuthConfigLinkageResult {
  /** true = linked, false = confirmed mismatch, null = indeterminate. */
  linked: boolean | null;
  message: string;
  details?: Record<string, unknown>;
}

/**
 * Assess whether a connected account's auth_config is one the agent's wired
 * MCP server actually resolves with. See the module header for the tri-state
 * contract. Pure: no I/O, fully unit-testable.
 */
export function assessAuthConfigLinkage(
  input: AuthConfigLinkageInput,
): AuthConfigLinkageResult {
  const { accountAuthConfigId, serverAuthConfigIds, serverId } = input;
  const serverLabel = serverId ? ` (${serverId})` : '';

  // Indeterminate: a missing side means we cannot honestly assert mismatch.
  if (serverAuthConfigIds == null || !accountAuthConfigId) {
    return {
      linked: null,
      message:
        'auth_config linkage not verified — ' +
        (serverAuthConfigIds == null
          ? "couldn't read the wired MCP server's auth config binding"
          : 'Composio returned no auth_config for the connected account'),
      details: {
        accountAuthConfigId: accountAuthConfigId ?? null,
        serverAuthConfigIds: serverAuthConfigIds ?? null,
        serverId: serverId ?? null,
      },
    };
  }

  // Fetched, but the server binds NO auth config → it resolves nothing.
  if (serverAuthConfigIds.length === 0) {
    return {
      linked: false,
      message:
        `The agent's wired MCP server${serverLabel} has no auth config bound, so it cannot resolve the ` +
        `connected account (bound to auth_config ${accountAuthConfigId}) — reconnect/rebind required.`,
      details: { accountAuthConfigId, serverAuthConfigIds, serverId: serverId ?? null },
    };
  }

  if (serverAuthConfigIds.includes(accountAuthConfigId)) {
    return {
      linked: true,
      message: `Connected account's auth_config (${accountAuthConfigId}) matches the wired MCP server binding.`,
      details: { accountAuthConfigId, serverAuthConfigIds, serverId: serverId ?? null },
    };
  }

  return {
    linked: false,
    message:
      `The connected account is bound to auth_config ${accountAuthConfigId}, but the agent's wired MCP ` +
      `server${serverLabel} resolves auth_config(s) [${serverAuthConfigIds.join(', ')}] — tool calls will fail ` +
      `with "No connected account found". Reconnect/rebind required.`,
    details: { accountAuthConfigId, serverAuthConfigIds, serverId: serverId ?? null },
  };
}
